<script setup lang="ts">
/**
 * BG §22 — the surface everything in the product sits on: system charcoal, square, hairline border.
 *
 * `measure` exists because the portal's page roots are required to be full-bleed (a rule its
 * page-width.spec.ts enforces across every view), which is right for tables and execution graphs
 * and wrong for prose and forms — a 3440px-wide text column is unreadable. Capping the measure on
 * the panel rather than the page keeps both true at once.
 *
 * `interactive` is for a panel that is itself the control — a picker card rendered `as="button"`. It
 * takes the command cyan (BG §22.4) on its hairline when hovered, and keeps it while it carries
 * `aria-current` (or `aria-pressed="true"`, when the card is a toggle), so the accessible signal and the
 * visible one are the same attribute and a screen never states a border colour of its own to say "this one".
 *
 * `canvas` is for a panel that frames a graph (Vue Flow, the knowledge graph): a canvas has no height
 * of its own and fills whatever box it is given, so the panel gives it one — most of the viewport,
 * never so tall it cannot be scrolled past, never so short the graph is a sliver — and clips what
 * pans outside it. Stated once here so no screen restates it as an arbitrary height utility.
 */
type Measure = 'full' | 'prose' | 'form'

withDefaults(
    defineProps<{
        /** `full` fills the canvas; `prose` caps at a readable line length; `form` is narrower still. */
        measure?: Measure
        /** Drops the padding, for panels that wrap a table drawing its own cell padding. */
        flush?: boolean
        /** The panel is the control itself: a hover and `aria-current` state, and a pointer. */
        interactive?: boolean
        /** Gives the panel the fixed, viewport-relative height a graph canvas fills, and clips its overflow. */
        canvas?: boolean
        as?: string
        /** Severity: `action` is the warning tone, `failed` the error tone. */
        tone?: 'action' | 'failed'
    }>(),
    { measure: 'full', flush: false, interactive: false, canvas: false, as: 'section' },
)
</script>

<template>
    <component
        :is="as"
        class="zh-panel"
        :data-measure="measure"
        :data-flush="flush ? 'on' : 'off'"
        :data-interactive="interactive ? 'on' : undefined"
        :data-canvas="canvas ? 'on' : 'off'"
        :data-tone="tone"
    >
        <slot />
    </component>
</template>

<style scoped>
.zh-panel {
    border: 1px solid var(--border);
    background: var(--system-charcoal);
    padding: 1rem;
}

.zh-panel[data-flush='on'] {
    padding: 0;
}

/* ~70 characters at the body size, which is the readable range for continuous text. */
.zh-panel[data-measure='prose'] {
    max-width: 44rem;
}

.zh-panel[data-measure='form'] {
    max-width: 34rem;
}

.zh-panel[data-interactive='on'] {
    color: inherit;
    cursor: pointer;
    transition: border-color var(--motion-ui) ease;
}

.zh-panel[data-interactive='on']:hover:not(:disabled),
.zh-panel[data-interactive='on'][aria-current]:not([aria-current='false']),
.zh-panel[data-interactive='on'][aria-pressed='true'] {
    border-color: var(--electric-cyan);
}

.zh-panel[data-interactive='on']:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

/*
 * Severity. The hairline takes the same state colour the status pill uses for the tone, so an error card and a
 * failed pill read as one statement, and no screen mixes a tint of its own to say "this one is a warning".
 */
.zh-panel[data-tone='failed'] {
    border-color: color-mix(in srgb, var(--state-failed) 50%, var(--border));
}

.zh-panel[data-tone='action'] {
    border-color: color-mix(in srgb, var(--state-action) 50%, var(--border));
    background: color-mix(in srgb, var(--state-action) 8%, var(--system-charcoal));
}

/* Most of the viewport, capped so the page below stays reachable, floored so the graph never collapses. */
.zh-panel[data-canvas='on'] {
    height: min(68vh, 47.5rem);
    min-height: 27.5rem;
    overflow: hidden;
}

@media (min-width: 48rem) {
    .zh-panel {
        padding: 1.25rem;
    }

    .zh-panel[data-flush='on'] {
        padding: 0;
    }

    .zh-panel[data-canvas='on'] {
        min-height: 30rem;
    }
}
</style>
